import { AfterViewInit, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { I18NService } from '@ebw-monorepo/i18n-translate';
import { EbwModalService } from '@ebw-monorepo/modal';
import { Collection, Feature, MapBrowserEvent, Overlay } from 'ol';
import { Coordinate } from 'ol/coordinate';
import { Condition } from 'ol/events/condition';
import { FeatureLike } from 'ol/Feature';
import Circle from 'ol/geom/Circle';
import Geometry from 'ol/geom/Geometry';
import LineString from 'ol/geom/LineString';
import MultiPoint from 'ol/geom/MultiPoint';
import Point from 'ol/geom/Point';
import Polygon from 'ol/geom/Polygon';
import { Draw, Modify, Select } from 'ol/interaction';
import { DrawEvent } from 'ol/interaction/Draw';
import { ModifyEvent } from 'ol/interaction/Modify';
import VectorLayer from 'ol/layer/Vector';
import { transform } from 'ol/proj';
import VectorSource from 'ol/source/Vector';
import { getArea, getLength } from 'ol/sphere.js';
import { Fill, Stroke, Style, Text } from 'ol/style';
import CircleStyle from 'ol/style/Circle';
import { StyleLike } from 'ol/style/Style';
import { take } from 'rxjs/operators';
import { IDrawingToolOptions } from '../../models/drawingToolOptions.model';
import { DrawingGeometries, DrawingService } from '../../services/drawing.service';
import { LayerStylesService } from '../../services/layer-styles.service';
import { MapService } from '../../services/map.service';
import { safeJsonClone } from '@ebw-monorepo/microservices-helpers/utils/parser';

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Drawing tool:
//
// The drawing tool allows a user to draw Point, Line, Polygon, Circle, and Text features to the OpenLayers map.
// It uses two Layers, which are the temporalEditingLayer for storing and showing the drawings and modifications of
// a feature that is being made, and the temporalDrawingLayer that stores and shows the features that are finished.
//
// There are 3 main interactions in this component: Draw, Modify, Select.
// - Draw: While active, allows the draw different geometries on the map.
// - Modify: While active, the user is allowed to modify a previously created feature by dragging the points or using the editing panel.
// - Select: While active, the user can select one of the previously created features to modify them.
//
// The tool have 3 states:
// - Stand-by: There is no drawing or edition ongoing. In this state, the Select interaction is active and the Draw and Modify are not.
// - Drawing: There is a drawing ongoing. In this state the Draw interacion is active, but the Select and Modify are not.
// - Editing: In this state, the user is editing a feature after being drawn or selected for editing. In this state the Select interaction is inactive,
//            the Modify interaction is active, and the Draw interaction can be active or inactive. Also, the editing panel is always shown in this state.
//
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

@Component({
  selector: 'ebw-drawing-tool',
  templateUrl: './drawing-tool.component.html',
  styleUrls: ['./drawing-tool.component.scss']
})
export class DrawingToolComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() options: IDrawingToolOptions;

  GEOMETRY_NAME_KEY = 'name';
  EDIT_FEATURE_ID = 'edit_feature';
  GEOMETRY_TYPE_KEY = 'geometry_type';
  TEXT_PROPERTIES_KEY = 'text_properties';
  DRAWING_INTERACTION_KEY = 'DRAWING_INTERACTION';
  MODIFY_INTERACTION_KEY = 'MODIFY_INTERACTION';
  SELECT_INTERACTION_KEY = 'SELECT_INTERACTION';

  /**
   * Array of drawing/editing operations that are performed with the drawing tool.
   * It works like a stack for the Undo functionality in order to restore previous features after an edit or to remove a feature after a draw.
   * It specifies the type of operation (draw or modify), the feature that was added, the previous feature, and the feature unique ID,
   * so it can be retrieved from the drawing layer.
   */
  operations: {
    type: string;
    newFeature: Feature;
    oldFeature: Feature;
    featureId: string;
  }[] = [];

  /** Internal counter of drawings. It is used to set the id of layers and ensures it is unique. */
  drawingCount: number = 0;

  /** Layer where the complete drawings are stored and displayed. It gets added when the component inits and gets removed when the component is destroyed. */
  temporalDrawingLayer: VectorLayer<any>;

  /** Source for the temporalDrawingLayer. */
  temporalDrawingSource: VectorSource;

  /** Layer where the drawings and modifications are temporarily stored before being added to the temporalDrawingLayer. */
  temporalEditingLayer: VectorLayer<any>;

  /** Source for the temporalEditingLayer */
  temporalEditingSource: VectorSource;

  pointTooltips: Overlay[] = [];
  drawingSelectPopup: Overlay = null;
  drawingSelectContent: HTMLElement = null;
  drawingSelectContainer: HTMLElement = null;
  drawingSelectCloser: HTMLElement = null;
  selectedFeatures: Feature[] = [];

  /** Global style that is added and removed when the disambiguation popup is shown when selecting a Feature for editing. */
  private selectedFeatureStyle = new Style({
    //A yellow stroke to highlight the feature geometry
    stroke: new Stroke({ color: this.drawingService.selectedFeatureColor(), width: 4 }),
    image: new CircleStyle({
      radius: 6,
      stroke: new Stroke({ color: this.drawingService.selectedFeatureColor(), width: 2 })
    })
  });

  /**
   * OpenLayers Condition for only allowing interactions to trigger on a left click.
   * @param event Click event
   * @returns True or false if the condition of the left click is fulfilled.
   */
  leftClickOnlyCondition: Condition = (event: MapBrowserEvent<any>) => {
    if (event.originalEvent.button == 0) {
      return true;
    } else {
      return false;
    }
  };

  /**
   * Global Drawing interaction for drawing any type of features.
   * This interaction gets initialized and added when a feature for drawing is selected and removed when a drawing is completed.
   */
  drawingInteraction: Draw = null;

  /**
   * Global Drawing interaction for modifying any type of features.
   * This interaction gets initialized and added when a feature is drawn and enters the "Editing" phase.
   */
  modifyInteraction: Modify = null;

  /**
   * Global Drawing interaction for selecting features that exist in the temporal drawing layer.
   * It is active while the drawing tool is open and disabled during drawing and editing.
   */
  selectInteraction: Select = null;

  /**
   * Indicates if the component is currently on the drawing state. This means that there is a Draw
   * interaction active.
   */
  isDrawing: boolean = false;

  /**
   * Indicates if the component is currently on the editing state. This means that there is a Modify
   * interaction active.
   */
  isEditing: boolean = false;

  isFeatureEdited: boolean = false;

  /**
   * Copy of the feature that is currently being edited during an Editing. This is done to restore the feature if
   * the editing is cancelled and to save the previous state of the feature for the Undo functionality.
   */
  editingFeatureCopy: Feature = null;

  /**
   * The type of figure that is being drawn by the component. It can be any of the Drawing Geometries in the
   * enumerator (Point, Line, Polygon, Circle, Text)
   */
  drawingFigure: DrawingGeometries = null;

  /**
   * Variable with the figure types. This is to be able to use the DrawingGeometries enumerator on the
   * HTML template.
   */
  figureTypes = DrawingGeometries;

  /**
   * Indicates if the drawing editing tool is open. This tool allows to modify the points and certain
   * characteristics of the figures being drawn on the map.
   */
  drawingEditOpen: boolean = false;

  /** Transparent style for visually "hidding" geometries. */
  private transparentStyle: Style = new Style({
    fill: new Fill({ color: 'rgba(0,0,0,0.0)' }),
    stroke: new Stroke({ color: 'rgba(0,0,0,0.0)' })
  });

  ////////////////////////////////
  // DRAWING EDITING VARIABLES //
  //////////////////////////////

  /** The EPSG values that are enabled for picking as a reference system. */
  EPSG_VALUES = [
    { value: 'EPSG:4326', label: 'EPSG:4326 - World Geodetic System 1984' },
    { value: 'EPSG:3857', label: 'EPSG:3857 - Pseudo-Mercator / Spherical Mercator' }
  ];

  /** Default EPSG for the drawing tool */
  DEFAULT_EPSG: string = 'EPSG:3857';

  /** Indicates the EPSG that is currently being used to display the coordinates of the drawn features. */
  currentEPSG = this.DEFAULT_EPSG;

  /** The title of the drawing editing tool header. This changes to reflect which figure type is being edited. */
  editDrawingTitle: string = '';

  /**
   * General options for the figure editing. Contains the custom name of the geometry and the reference
   * system in which the coordinates are displayed.
   */
  generalOptions = {
    geometryName: '',
    EPSG: this.DEFAULT_EPSG
  };

  /**
   * Options for the Point drawing. Contains the coordinates of the point in the selected EPSG.
   * The coordinates of this object are used to build the editing panel form fields.
   */
  pointOptions = {
    coordinate: [0, 0]
  };

  /**
   * Options for the Line drawing. Contains the coordinates of the points of the line in the selected EPSG.
   * The coordinates of this object are used to build the editing panel form fields.
   */
  lineOptions = {
    coordinates: []
  };

  /**
   * Options for the Polygon drawing. Contains the coordinates of the points of the polygon in the selected EPSG.
   * The coordinates of this object are used to build the editing panel form fields.
   */
  polygonOptions = {
    coordinates: []
  };

  /**
   * Options for the Circle drawing. Contains the coordinates of the center of the circle in the selected EPSG
   * and the radius of the circle in meters.
   * The center and the radius properties of this object are used to build the editing panel form fields.
   */
  circleOptions = {
    radius: 0,
    center: [0, 0]
  };

  //Values for text
  /** Font type values and labels */
  FONT_TYPE_VALUES = [
    { value: 'Arial', label: 'Arial' },
    { value: 'Courier New', label: 'Courier New' },
    { value: 'Verdana', label: 'Verdana' }
  ];
  /** Default font type */
  DEFAULT_FONT_TYPE: string = 'Arial';

  /** Font weight values and labels */
  FONT_WEIGHT_VALUES = [
    { value: 'normal', label: 'Normal' },
    { value: 'bold', label: 'Bold' },
    { value: 'italic', label: 'Italic' }
  ];
  /** Default font weight */
  DEFAULT_FONT_WEIGHT: string = 'normal';

  /** Default font size */
  DEFAULT_FONT_SIZE: number = 16;
  /** Default font size unit */
  DEFAULT_FONT_SIZE_UNIT: string = 'px';
  /** Default text color */
  DEFAULT_TEXT_COLOR: string = this.drawingService.defaultTextColor();
  /** Default border text color - initially transparent */
  DEFAULT_TEXT_BORDER: string = 'transparent';

  /**
   * Options for the text label feature.
   * The properties of this object are used to build the editing panel form fields.
   */
  textOptions: {
    label: string;
    fontType: string;
    fontWeight: string;
    fontSize: number;
    fontSizeUnit: string;
    color: string;
    borderColor: string;
    position: number[];
  } = {
    label: 'Text Label',
    fontType: this.DEFAULT_FONT_TYPE,
    fontWeight: this.DEFAULT_FONT_WEIGHT,
    fontSize: this.DEFAULT_FONT_SIZE,
    fontSizeUnit: this.DEFAULT_FONT_SIZE_UNIT,
    color: this.DEFAULT_TEXT_COLOR,
    borderColor: this.DEFAULT_TEXT_BORDER,
    position: [0, 0]
  };

  figureTranslation: { [id: string]: string } = {};

  /** Import the giornale-lavori to be used in the component. */
  constructor(
    private mapService: MapService,
    private drawingService: DrawingService,
    private styleService: LayerStylesService,
    private ebwModalService: EbwModalService,
    private translateService: I18NService
  ) {}

  /**
   * Initializes the component.
   *
   * 1. Creates the temporalDrawingLayer and add is to the OpenLayers map.
   * 2. Initializes the Select interaction and event handler, sets it inactive, and adds it to the OpenLayers map.
   */
  ngOnInit(): void {
    this.initFigureTranslations();

    //Create temporal drawing layer when drawing tool is open
    this.temporalDrawingSource = new VectorSource({ wrapX: false });
    this.temporalDrawingLayer = new VectorLayer({
      source: this.temporalDrawingSource
    });
    this.temporalDrawingLayer.setZIndex(20);
    this.mapService.openLayersMap.addLayer(this.temporalDrawingLayer);

    //Create the select interaction for the drawn features.
    this.selectInteraction = new Select({
      layers: [this.temporalDrawingLayer],
      hitTolerance: 10,
      multi: true
    });
    this.selectInteraction.setActive(false);
    this.selectInteraction.on('select', (selected) => {
      let selectedFeatures = selected.selected;
      if (selectedFeatures.length == 1) {
        this.setupFeatureToEdit(selectedFeatures[0]);
      } else if (selectedFeatures.length >= 2) {
        this.selectedFeatures = selectedFeatures;
        const popupPosition = selected.mapBrowserEvent.coordinate;
        this.drawingSelectPopup.setPosition(popupPosition);
        this.selectInteraction.setActive(false);
      }
      return;
    });
    this.mapService.addMapInteraction(this.SELECT_INTERACTION_KEY, this.selectInteraction);
  }

  /**
   * Get the HTML elements for the Drawing Select popup after the view has been initialized in the component lifecycle.
   */
  ngAfterViewInit(): void {
    //Get the html elements for the drawing select (disambiguation) popup.
    this.drawingSelectCloser = document.getElementById('select-popup-close');
    this.drawingSelectContainer = document.getElementById('select-popup');
    this.drawingSelectContent = document.getElementById('select-popup-content');

    //Create the drawing select popup with the html elements from above.
    this.drawingSelectPopup = new Overlay({
      element: this.drawingSelectContainer,
      autoPan: {
        animation: {
          duration: 250
        }
      }
    });
    this.mapService.openLayersMap.addOverlay(this.drawingSelectPopup);
  }

  /** Clean the OpenLayers map when the component is destroyed by ending the current drawing, removing the interactions, overlays, and the temporal layers. */
  ngOnDestroy(): void {
    //In case the component is destroyed while it is drawing.
    this.endDraw();
    //Remove the select interaction from the map
    this.mapService.removeMapInteraction(this.SELECT_INTERACTION_KEY);
    //Remove the temporal drawing layer
    this.mapService.openLayersMap.removeLayer(this.temporalDrawingLayer);
    //Remove the overlay
    this.mapService.openLayersMap.removeOverlay(this.drawingSelectPopup);
    //Component destroyed with the tool still flagged as open (e.g. the map component is destroyed): release the map click owner
    if (this.drawingService.drawingToolOpen) {
      this.drawingService.closeDrawingTool();
    }
  }

  initFigureTranslations() {
    this.figureTranslation['point'] = this.translateService.getTranslateParameter('map.drawing_tool.point');

    this.figureTranslation['line'] = this.translateService.getTranslateParameter('map.drawing_tool.line');
    this.figureTranslation['polygon'] = this.translateService.getTranslateParameter('map.drawing_tool.polygon');
    this.figureTranslation['circle'] = this.translateService.getTranslateParameter('map.drawing_tool.circle');
  }

  /**
   * Function to be triggered when the close button of the drawing tool is clicked or to close the drawing tool.
   * It first displays a Confirm Modal that informs that all drawings will be deleted if the tool is closed.
   * It then sets the global drawing service variable to destroy the component from its parent component.
   */
  closeDrawingTool() {
    let title = this.translateService.getTranslateParameter('map.drawing_tool.confirm_close_title');
    let content = this.translateService.getTranslateParameter('map.drawing_tool.confirm_close_content');

    const confirmModal = this.ebwModalService.openConfirmModal({
      title: title,
      content: content
    });
    confirmModal
      .afterClosed()
      .pipe(take(1))
      .subscribe((result) => {
        //If the confirmation is successful
        if (result == true) {
          this.drawingService.closeDrawingTool();
        }
        //Else do nothing
      });
  }

  /** Check if the drawing interaction is currently active */
  hasDrawInteraction() {
    return this.drawingInteraction.getActive();
  }

  /**
   * Function to start and end a drawing of any figure with the drawing tool.
   * Depending on the figure to draw, different parameters are initialized and different variables are used.
   *
   * It does one of the following:
   * - If a drawing is already active, and the input figure is of the same type: cancels the draw.
   * - If a drawing is already active, and the input figure is of a different type: cancel the current draw and initlize the new one.
   * - If a drawing is not active: initialize a new drawing of the input figure.
   * @param figure the figure type to draw. It can be any of the DrawingGeometries (point, line, polygon, circle, text).
   */
  toggleDraw(figure: DrawingGeometries) {
    //if it is opening a new drawing
    if (!this.isDrawing && !this.isEditing) {
      //Set the figure type
      this.drawingFigure = figure;
      //Start the draw type
      this.startDraw(figure);
    } else {
      //A drawing is in place, so launch the confirm modal
      let cancelTitle: string = this.translateService.getTranslateParameter('map.drawing_tool.cancel_drawing_title');
      let cancelContent: string = this.translateService.getTranslateParameter(
        'map.drawing_tool.cancel_drawing_content'
      );
      const confirmModal = this.ebwModalService.openConfirmModal({
        title: cancelTitle,
        content: cancelContent
      });
      confirmModal
        .afterClosed()
        .pipe(take(1))
        .subscribe((result) => {
          //If the confirmation is successful
          if (result == true) {
            //Restore the feature that was being edited
            if (this.isEditing) {
              this.temporalDrawingSource.addFeature(this.editingFeatureCopy);
            }
            if (this.drawingFigure == figure) {
              //Only cancel the current draw
              //TODO confirm
              this.endDraw();
            } else {
              //Cancel the current draw first
              this.endDraw();
              //Set the figure type
              this.drawingFigure = figure;
              //Start the draw type
              this.startDraw(figure);
            }
          }
          //Else do nothing
        });
    }
  }

  /**
   * Cancel a draw or editing that is currently being done after a confirmation modal has been displayed and confirmed.
   *
   * In case there was an editing ongoing, it restores the original feature.
   */
  cancelDraw() {
    let cancelTitle: string = this.translateService.getTranslateParameter('map.drawing_tool.cancel_drawing_title');
    let cancelContent: string = this.translateService.getTranslateParameter('map.drawing_tool.cancel_drawing_content');
    const confirmModal = this.ebwModalService.openConfirmModal({
      title: cancelTitle,
      content: cancelContent
    });
    confirmModal
      .afterClosed()
      .pipe(take(1))
      .subscribe((result) => {
        //If the confirmation is successful
        if (result == true) {
          //Restore the feature that was being edited
          if (this.isEditing) {
            this.temporalDrawingSource.addFeature(this.editingFeatureCopy);
          }
          this.endDraw();
        }
        //Else do nothing
      });
  }

  /**
   * Function to call to delete a drawing.
   */
  deleteDraw() {
    let deleteTitle: string = this.translateService.getTranslateParameter('map.drawing_tool.delete_drawing_title');
    let deleteContent: string = this.translateService.getTranslateParameter('map.drawing_tool.delete_drawing_content');
    const confirmModal = this.ebwModalService.openConfirmModal({
      title: deleteTitle,
      content: deleteContent
    });
    confirmModal
      .afterClosed()
      .pipe(take(1))
      .subscribe((result) => {
        //If the confirmation is successful
        if (result == true) {
          this.operations.push({
            type: 'DELETE',
            newFeature: null,
            oldFeature: this.editingFeatureCopy,
            featureId: this.editingFeatureCopy.getId().toString()
          });

          this.endDraw();
        }
        //Else do nothing
      });
  }

  /**
   * Set up drawing and configures the drawing parameters and functionalities depending on the
   * figure type, that is a DrawingGeometries type.
   *
   * The temporalEditingLayer gets initialized and added to the map everytime a new drawing is added.
   * @param figure The figure type to draw. Can be point, polygon, circle, text.
   */
  startDraw(figure: DrawingGeometries) {
    //Sets the drawing variable to true to indicate that there is a drawing ongoing.
    this.isDrawing = true;
    this.isFeatureEdited = true; //for the operation to be saved.
    this.selectInteraction.setActive(false);

    //Add the editing layer
    this.temporalEditingSource = new VectorSource({ wrapX: false });
    this.temporalEditingLayer = new VectorLayer({
      source: this.temporalEditingSource
    });

    this.temporalEditingLayer.setZIndex(20);

    this.mapService.openLayersMap.addLayer(this.temporalEditingLayer);

    //Initialize the drawing and modify interactions depending on the drawing figure
    switch (figure) {
      case DrawingGeometries.POINT: {
        this.editDrawingTitle = this.translateService.getTranslateParameter(
          'map.drawing_tool.edit_drawing_title',
          this.figureTranslation['point']
        );
        //Initialize the drawing interaction
        this.drawingInteraction = new Draw({
          source: this.temporalEditingSource,
          type: 'Point',
          condition: this.leftClickOnlyCondition
        });

        //After the draw is completed (drawend event).
        this.drawingInteraction.on('drawend', (drawnFeature: DrawEvent) => {
          const drawnPoint = drawnFeature['feature'] as Feature<Point>;
          drawnPoint.setStyle(this.transparentStyle); //this is to "hide" the ghost point of the interaction that gets added.
          const coord = drawnPoint.getGeometry().getCoordinates();
          //Save the drawn coordinate in the pointOptions with the currently selected EPSG.
          this.pointOptions.coordinate = transform(coord, MapService.EPSG_3857_KEY, this.currentEPSG);
          (drawnFeature['feature'] as Feature).setStyle(
            this.styleService.buildStyleFromFeatureStyle(this.drawingService.orangeFillFeatureStyle()) //TODO: change the style?
          );
          //Remove the draw interaction after the drawing is done.
          this.mapService.removeMapInteraction(this.DRAWING_INTERACTION_KEY);
          //Create and add the modify interaction to the map, and set the 'modifyend' event handler.
          this.modifyInteraction = new Modify({
            source: this.temporalEditingSource,
            style: this.styleService.buildStyleFromFeatureStyle(this.drawingService.blueFillFeatureStyle())
          });
          this.modifyInteraction.on('modifyend', (modify: ModifyEvent) => {
            let featureCollection = modify.features as Collection<Feature>;
            const coord = (featureCollection.item(0) as Feature<Point>).getGeometry().getCoordinates();
            //Save the modified coordinate in the current EPSG set
            this.pointOptions.coordinate = transform(coord, MapService.EPSG_3857_KEY, this.currentEPSG);
          });
          this.mapService.addMapInteraction(this.MODIFY_INTERACTION_KEY, this.modifyInteraction);
        });

        this.mapService.addMapInteraction(this.DRAWING_INTERACTION_KEY, this.drawingInteraction);
        break;
      }

      //In practice, the Line drawing is done by drawing the points of the line and joining them rather than drawing
      // a line geometry directly. This allows flexibility on the edition.
      case DrawingGeometries.LINE: {
        this.editDrawingTitle = this.translateService.getTranslateParameter(
          'map.drawing_tool.edit_drawing_title',
          this.figureTranslation['line']
        );
        //Initialize the drawing interaction.
        this.drawingInteraction = new Draw({
          source: this.temporalEditingSource,
          type: 'Point', //Draw points
          condition: this.leftClickOnlyCondition
        });
        //After the draw is completed (drawend event).
        this.drawingInteraction.on('drawend', (drawnFeature: DrawEvent) => {
          const drawnPoint = drawnFeature['feature'] as Feature<Point>;
          drawnPoint.setStyle(this.transparentStyle);
          this.lineOptions.coordinates.push(
            transform(drawnPoint.getGeometry().getCoordinates(), MapService.EPSG_3857_KEY, this.currentEPSG)
          );

          //Deep copy of coordinates
          let lineCoordinates: Coordinate[] = safeJsonClone(this.lineOptions.coordinates);
          //Set the coordinates in EPSG 3857 to work with them
          lineCoordinates = lineCoordinates.map((coord) => {
            return transform(coord, this.currentEPSG, MapService.EPSG_3857_KEY);
          });

          //Feature to be added with the new points
          let lineFeature: Feature = null;

          if (this.lineOptions.coordinates.length == 1) {
            //In only one point is added, then create a point
            lineFeature = new Feature<Point>(new Point(lineCoordinates[0]));
          } else {
            //In more than 1 points are added, create a line
            lineFeature = new Feature<LineString>(new LineString(lineCoordinates));
          }
          //Set the style for the line feature with buildLineModifyStyle (adds the point index indicators).
          lineFeature.setStyle((feat) => {
            return this.buildLineModifyStyle(feat);
          });

          //Clear the editing source and add the line feature that is created.
          this.temporalEditingSource.clear();
          let cloneFeature = lineFeature.clone();
          cloneFeature.setId(this.EDIT_FEATURE_ID);
          this.temporalEditingSource.addFeature(cloneFeature);

          //Add the modify interaction only when the first point of the line is added
          if (!this.modifyInteraction) {
            this.modifyInteraction = new Modify({
              source: this.temporalEditingSource,
              style: this.styleService.buildStyleFromFeatureStyle(this.drawingService.blueFillFeatureStyle())
            });
            //When a modification is made
            this.modifyInteraction.on('modifyend', (modify: ModifyEvent) => {
              let featureCollection = modify.features as Collection<Feature>;
              const coords = (featureCollection.item(0) as Feature<LineString>).getGeometry().getCoordinates();
              //Set the line coordinates as the currently set EPSG
              this.lineOptions.coordinates = coords.map((coord) => {
                return transform(coord, MapService.EPSG_3857_KEY, this.currentEPSG);
              });

              featureCollection.item(0).setStyle((feat) => {
                return this.buildLineModifyStyle(feat);
              });
            });
            this.mapService.addMapInteraction(this.MODIFY_INTERACTION_KEY, this.modifyInteraction);
          }
        });

        this.mapService.addMapInteraction(this.DRAWING_INTERACTION_KEY, this.drawingInteraction);
        break;
      }

      //In practice, the Polygon drawing is done by drawing the points of the polygon and joining them rather than drawing
      // a polygon geometry directly. This allows flexibility on the edition.
      case DrawingGeometries.POLYGON: {
        this.editDrawingTitle = this.translateService.getTranslateParameter(
          'map.drawing_tool.edit_drawing_title',
          this.figureTranslation['polygon']
        );
        //Initialize drawing interaction of points
        this.drawingInteraction = new Draw({
          source: this.temporalEditingSource,
          type: 'Point', //Drawing points!
          condition: this.leftClickOnlyCondition
        });

        //After the draw is completed (drawend event).
        this.drawingInteraction.on('drawend', (drawnFeature: DrawEvent) => {
          const drawnPoint = drawnFeature['feature'] as Feature<Point>;
          drawnPoint.setStyle(this.transparentStyle);
          const coord = drawnPoint.getGeometry().getCoordinates();
          //Add the coordinate to the list of coordinates in the selected EPSG
          this.polygonOptions.coordinates.push(transform(coord, MapService.EPSG_3857_KEY, this.currentEPSG));

          //Deep copy of coordinates
          let polygonCoordinates: Coordinate[] = safeJsonClone(this.polygonOptions.coordinates);
          //Set the coordinates in EPSG 3857 to work with them
          polygonCoordinates = polygonCoordinates.map((coord) => {
            return transform(coord, this.currentEPSG, MapService.EPSG_3857_KEY);
          });

          //Feature to be added with the new points
          let polygonFeature = null;

          if (this.polygonOptions.coordinates.length == 1) {
            //In only one point is added, then create a point
            polygonFeature = new Feature<Point>(new Point(polygonCoordinates[0]));
          } else if (this.polygonOptions.coordinates.length == 2) {
            //In only 2 points are added, create a line
            polygonFeature = new Feature<LineString>(new LineString(polygonCoordinates));
          } else {
            //In there are more than 2 points, create a Polygon
            polygonCoordinates.push(polygonCoordinates[0]);
            polygonFeature = new Feature<Polygon>(new Polygon([polygonCoordinates]));
          }
          //Set the style with buildPolygonModifyStyle (it adds the point index to each of the polygon points).
          polygonFeature.setStyle((feat) => {
            return this.buildPolygonModifyStyle(feat);
          });

          //Clear the temporal editing source and add the newly created polygon.
          this.temporalEditingSource.clear();
          let cloneFeature = polygonFeature.clone();
          cloneFeature.setId(this.EDIT_FEATURE_ID);
          this.temporalEditingSource.addFeature(cloneFeature);

          //Add the modify interaction only when the first point of the polygon is added
          if (!this.modifyInteraction) {
            this.modifyInteraction = new Modify({
              source: this.temporalEditingSource,
              style: this.styleService.buildStyleFromFeatureStyle(this.drawingService.blueFillFeatureStyle())
            });
            //When a modification is made
            this.modifyInteraction.on('modifyend', (modify: ModifyEvent) => {
              let featureCollection = modify.features as Collection<Feature<Polygon>>;
              //Save the coordinates of the internal ring
              let optionsCoordinates = featureCollection.item(0).getGeometry().getCoordinates()[0];
              //Remove the last coordinate (the one that closes the polygon)
              optionsCoordinates.splice(optionsCoordinates.length - 1, 1);
              //Set the polygon coordinates as the currently set EPSG
              this.polygonOptions.coordinates = optionsCoordinates.map((coord) => {
                return transform(coord, MapService.EPSG_3857_KEY, this.currentEPSG);
              });

              featureCollection.item(0).setStyle((feat) => {
                return this.buildPolygonModifyStyle(feat);
              });
            });
            this.mapService.addMapInteraction(this.MODIFY_INTERACTION_KEY, this.modifyInteraction);
          }
        });
        this.mapService.addMapInteraction(this.DRAWING_INTERACTION_KEY, this.drawingInteraction);
        break;
      }

      case DrawingGeometries.CIRCLE: {
        this.editDrawingTitle = this.translateService.getTranslateParameter(
          'map.drawing_tool.edit_drawing_title',
          this.figureTranslation['circle']
        );
        //Initialize the drawing interaction of a circle.
        this.drawingInteraction = new Draw({
          source: this.temporalEditingSource,
          type: 'Circle',
          condition: this.leftClickOnlyCondition
        });

        //After the draw is completed
        this.drawingInteraction.on('drawend', (drawnFeature: DrawEvent) => {
          const feature = drawnFeature['feature'] as Feature<Circle>;
          feature.setStyle([
            //Set an array of styles.
            //Main fill style for the circle
            this.styleService.buildStyleFromFeatureStyle(this.drawingService.orangeFillFeatureStyle()),
            //Style for painting the central point
            new Style({
              geometry: (feature: Feature<Circle>) => {
                return new Point(feature.getGeometry().getCenter());
              },
              image: new CircleStyle({
                radius: 4,
                fill: new Fill({
                  color: this.drawingService.circleCenterColor()
                })
              })
            })
          ]);
          //Set the circleOptions properties.
          const center = feature.getGeometry().getCenter();
          this.circleOptions.center = transform(center, MapService.EPSG_3857_KEY, this.currentEPSG);
          this.circleOptions.radius = this.drawingService.convertRadiusToMeters(
            feature.getGeometry().getRadius(),
            this.mapService.openLayersMap.getView()
          );

          //Remove the drawing interaction because now the editing is done with the modify interaction.
          this.mapService.removeMapInteraction(this.DRAWING_INTERACTION_KEY);

          //Add the modify interaction only when the circle drawing has finished.
          if (!this.modifyInteraction) {
            this.modifyInteraction = new Modify({
              source: this.temporalEditingSource,
              style: this.styleService.buildStyleFromFeatureStyle(this.drawingService.blueFillFeatureStyle())
            });
            //When a modification is made
            this.modifyInteraction.on('modifyend', (modify: ModifyEvent) => {
              let featureCollection = modify.features as Collection<Feature>;
              const center = (featureCollection.item(0) as Feature<Circle>).getGeometry().getCenter();
              //Save the center to be displayed in the current EPSG
              this.circleOptions.center = transform(center, MapService.EPSG_3857_KEY, this.currentEPSG);
              this.circleOptions.radius = this.drawingService.convertRadiusToMeters(
                (featureCollection.item(0) as Feature<Circle>).getGeometry().getRadius(),
                this.mapService.openLayersMap.getView()
              );
            });
            this.mapService.addMapInteraction(this.MODIFY_INTERACTION_KEY, this.modifyInteraction);
          }
        });
        this.mapService.addMapInteraction(this.DRAWING_INTERACTION_KEY, this.drawingInteraction);
        break;
      }

      //The text customization is done by drawing a point and setting a style that gets built from the textOptions.
      // It is very different from the other geometries and is usually an exception on the styles that get added.
      case DrawingGeometries.TEXT: {
        this.editDrawingTitle = this.translateService.getTranslateParameter('map.drawing_tool.text_customization');
        //Initialize Point drawing interaction with the default text style.
        this.drawingInteraction = new Draw({
          source: this.temporalEditingSource,
          type: 'Point',
          condition: this.leftClickOnlyCondition,
          style: this.buildTextStyle()
        });

        //After the draw is completed
        this.drawingInteraction.on('drawend', (drawnFeature: DrawEvent) => {
          const textFeature = drawnFeature['feature'] as Feature<Point>;
          textFeature.setStyle(
            this.buildTextStyle() //Set again the style with the textOptions values.
          );
          const textCoordinates = textFeature.getGeometry().getCoordinates();
          this.textOptions.position = transform(textCoordinates, MapService.EPSG_3857_KEY, this.currentEPSG);
          //Remove the drawing interaction after the point is placed.
          this.mapService.removeMapInteraction(this.DRAWING_INTERACTION_KEY);
        });

        //Create and add the modify interaction to the map, and set the 'modifyend' event handler.
        this.modifyInteraction = new Modify({
          source: this.temporalEditingSource,
          style: this.styleService.buildStyleFromFeatureStyle(this.drawingService.blueFillFeatureStyle())
        });
        this.modifyInteraction.on('modifyend', (modify: ModifyEvent) => {
          let featureCollection = modify.features as Collection<Feature>;
          const coord = (featureCollection.item(0) as Feature<Point>).getGeometry().getCoordinates();
          //Save the modified coordinate in the current EPSG set
          this.pointOptions.coordinate = transform(coord, MapService.EPSG_3857_KEY, this.currentEPSG);
          this.textOptions.position = this.pointOptions.coordinate;
        });
        this.mapService.addMapInteraction(this.MODIFY_INTERACTION_KEY, this.modifyInteraction);
        this.mapService.addMapInteraction(this.DRAWING_INTERACTION_KEY, this.drawingInteraction);
        break;
      }
    }

    //Open the drawing edit tools at the end with the figures and corresponding options objects initialized.
    this.openDrawingEdit();
  }

  /**
   * End an ongoing draw and reset all the corresponding variables.
   * - Reset the isDrawing and isEditing variables.
   * - Remove the temporalEditingLayer and clears the source.
   * - Set the select interaction active again.
   * - Reset the option object of each of the geometries.
   * - Remove tooltips, drawing and modify interactions.
   */
  endDraw() {
    //Set the global and local drawing and editing variables
    this.isDrawing = false;
    this.isEditing = false;

    //remove editing layer
    if (this.temporalEditingSource) {
      this.temporalEditingSource.clear();
    }
    this.mapService.openLayersMap.removeLayer(this.temporalEditingLayer);

    //add the select interaction
    this.selectInteraction.setActive(true);

    //Set the default values of generalOptions
    this.generalOptions.EPSG = this.DEFAULT_EPSG;
    this.generalOptions.geometryName = '';
    this.currentEPSG = this.DEFAULT_EPSG;

    //Reset point options
    this.pointOptions.coordinate = [0, 0];

    //Reset line options
    this.lineOptions.coordinates = [];

    //Reset polygon options
    this.polygonOptions.coordinates = [];

    //Reset circle options
    this.circleOptions.radius = 0;
    this.circleOptions.center = [0, 0];

    //Reset text options
    this.textOptions.label = 'Text Label';
    this.textOptions.fontType = this.DEFAULT_FONT_TYPE;
    this.textOptions.fontWeight = this.DEFAULT_FONT_WEIGHT;
    this.textOptions.fontSize = this.DEFAULT_FONT_SIZE;
    this.textOptions.color = this.DEFAULT_TEXT_COLOR;
    this.textOptions.borderColor = this.DEFAULT_TEXT_BORDER;
    this.textOptions.position = [0, 0];

    //Remove tooltips
    this.removeAllTooltips();

    //Reset
    this.mapService.removeMapInteraction(this.DRAWING_INTERACTION_KEY);
    this.mapService.removeMapInteraction(this.MODIFY_INTERACTION_KEY);

    this.closeDrawingEdit();

    //Delete the drawing click event
    this.drawingFigure = null;
    this.drawingInteraction = null;
    this.modifyInteraction = null;
  }

  /**
   * Confirms a drawing or editing that is ongoing and adds it to the temporal drawing layer.
   * It also adds the operation to the operations stack based with all the necessary properties.
   * @returns void
   */
  confirmCurrentDraw() {
    //Check if the drawing is correct (see the function for more information on when a drawing is correct).
    if (this.checkIfCurrentDrawIsCorrect()) {
      //Set a new drawing ID if it is a new drawing or get the previous id if it is a modification.
      const drawingId = (this.isEditing ? this.editingFeatureCopy.getId() : this.drawingCount).toString();
      let drawingName = this.generalOptions.geometryName;

      const isNameTooLong = drawingName.length > 255;
      if (isNameTooLong) {
        let errorTitle: string = this.translateService.getTranslateParameter('map.drawing_tool.error_long_name_title');
        let errorContent: string = this.translateService.getTranslateParameter(
          'map.drawing_tool.error_long_name_content'
        );
        this.ebwModalService.openErrorModal({
          title: errorTitle,
          content: errorContent
        });
        return;
      }

      //Check for duplicate geometry names - In case a name is edited and overlaps another name.
      const isNameDuplicate =
        this.temporalDrawingSource.getFeatures().findIndex((el) => {
          return el.get(this.GEOMETRY_NAME_KEY) == drawingName;
        }) != -1;

      if (isNameDuplicate) {
        let errorTitle: string = this.translateService.getTranslateParameter('map.drawing_tool.error_duplicate_title');
        let errorContent: string = this.translateService.getTranslateParameter(
          'map.drawing_tool.error_duplicate_content'
        );
        this.ebwModalService.openErrorModal({
          title: errorTitle,
          content: errorContent
        });
        return;
      }

      this.drawingCount++;
      if (drawingName == '') {
        drawingName = 'Drawing ' + this.drawingCount.toString(); //The name is unique.
      }

      //Data for the operations stack.
      let operationType = '';
      let oldFeature = null;
      if (this.isEditing) {
        //Is a modification
        operationType = 'MODIFY';
        oldFeature = this.editingFeatureCopy.clone();
      } else {
        //Is a new draw
        operationType = 'DRAW';
      }

      //Get all the features on the temporal editing source
      const drawnFeatures = this.temporalEditingSource.getFeatures();

      //Add the type of geometry that is being drawn based on the drawingFigure variable.
      // This selection is necessary because sometimes a point is added to the editing layer from the drawing and/or modify interactions.
      const drawingType = this.drawingService.getGeometryTypeFromDrawingGeometry(this.drawingFigure);
      let featureToAdd = drawnFeatures.find((el) => {
        return el.getGeometry().getType() == drawingType;
      });

      if (featureToAdd == this.editingFeatureCopy) {
        console.log('no mod');
      }

      //Create a new feature to add to the temporal drawing layer from a clone of the geometry.
      const newFeature = new Feature((featureToAdd as Feature<Geometry>).getGeometry().clone());
      //Add the properties of the previous feature and the styles
      newFeature.set(this.GEOMETRY_NAME_KEY, drawingName);
      newFeature.set(this.GEOMETRY_TYPE_KEY, this.drawingFigure);
      newFeature.setId(drawingId);

      //Set the styles depending on the figure type (text is always the exception).
      if (this.drawingFigure == this.figureTypes.TEXT) {
        newFeature.setStyle(this.buildTextStyle());
        newFeature.set(this.TEXT_PROPERTIES_KEY, safeJsonClone(this.textOptions)); //Deep copy the text options so it gets the values and not the reference of the object.
      } else {
        newFeature.setStyle(this.styleService.buildStyleFromFeatureStyle(this.drawingService.blueFillFeatureStyle()));
      }

      //Finally, add the feature to the temporal drawing source
      this.temporalDrawingSource.addFeature(newFeature);
      this.drawingService.drawingGeometry$.next(newFeature);

      //Add the operation to the operations stack if the feature was modified.
      if (this.isFeatureEdited) {
        this.operations.push({
          type: operationType,
          newFeature: newFeature,
          oldFeature: oldFeature,
          featureId: drawingId
        });
      }

      //At the end, end the draw to restart all the variables.
      this.endDraw();
    } else {
      //Display a modal if the drawing is not correct.
      let errorTitle: string = this.translateService.getTranslateParameter(
        'map.drawing_tool.error_wrong_geometry_title'
      );
      let errorContent: string = this.translateService.getTranslateParameter(
        'map.drawing_tool.error_wrong_geometry_content'
      );
      this.ebwModalService.openErrorModal({ title: errorTitle, content: errorContent });
    }
  }

  /** Update all the coordinates in the options objects when the reference system is changed in the form. */
  updateCoordinates() {
    //Set the conversion 'from' and 'to'
    const fromEPSG = this.currentEPSG;
    const toEPSG = this.generalOptions.EPSG;

    //Transform point coordinates
    this.pointOptions.coordinate = transform(this.pointOptions.coordinate, fromEPSG, toEPSG);
    //Transform line coordiantes
    let tempLineCoords = [];
    this.lineOptions.coordinates.forEach((coord) => {
      tempLineCoords.push(transform(coord, fromEPSG, toEPSG));
    });
    this.lineOptions.coordinates = tempLineCoords;
    //Transform polygon coordinates
    let tempPolyCoords = [];
    this.polygonOptions.coordinates.forEach((coord) => {
      tempPolyCoords.push(transform(coord, fromEPSG, toEPSG));
    });
    this.polygonOptions.coordinates = tempPolyCoords;
    //transform circle coordinates
    this.circleOptions.center = transform(this.circleOptions.center, fromEPSG, toEPSG);
    //transform text coordinates
    this.textOptions.position = transform(this.textOptions.position, fromEPSG, toEPSG);

    //Set the new current EPSG after tranforming coordinates
    this.currentEPSG = this.generalOptions.EPSG;
  }

  /** Open the edit feature panel. */
  openDrawingEdit() {
    this.drawingEditOpen = true;
  }

  /** Closes the edit feature panel. */
  closeDrawingEdit() {
    this.drawingEditOpen = false;
  }

  /**
   * Function to update the point coordinates of a Point drawing or edit..
   * The modification comes from the form field in the edit feature panel and is applied to the Point feature currently being edited.
   */
  manualUpdatePoint() {
    this.isFeatureEdited = true;
    let featureList = this.temporalEditingSource.getFeatures();
    //If there is a feature to update
    if (featureList.length > 0) {
      //Transform the coordinates from the EPSG that is specified in options to 3857
      const pointCoordinates = transform(
        this.pointOptions.coordinate,
        this.generalOptions.EPSG,
        MapService.EPSG_3857_KEY
      );
      const feature = featureList[0] as Feature<Point>;
      feature.getGeometry().setCoordinates(pointCoordinates);
    }
  }

  /**
   * Function to update the coordinates of a Line drawing or edit.
   * The modification comes from the form fields in the edit feature panel and is applied to the LineString feature currently being edited.
   */
  manualUpdateLine() {
    this.isFeatureEdited = true;
    let editFeature = this.temporalEditingSource.getFeatureById(this.EDIT_FEATURE_ID);
    //If there is a feature to update
    if (editFeature) {
      //Transform the coordinates from the EPSG that is specified in options to 3857
      const newCoordinates = this.lineOptions.coordinates.map((coord) => {
        return transform(coord, this.generalOptions.EPSG, MapService.EPSG_3857_KEY);
      });
      const feature = editFeature as Feature<LineString>;
      feature.getGeometry().setCoordinates(newCoordinates);
    }
  }

  /**
   * Function to update the coordinates of a Polygon drawing or edit.
   * The modification comes from the form fields in the edit feature panel and is applied to the Polygon feature currently being edited.
   */
  manualUpdatePolygon() {
    this.isFeatureEdited = true;
    let editFeature = this.temporalEditingSource.getFeatureById(this.EDIT_FEATURE_ID);
    //If there is a feature to update
    if (editFeature) {
      //Transform the coordinates from the EPSG that is specified in options to 3857
      const newCoordinates = this.polygonOptions.coordinates.map((coord) => {
        return transform(coord, this.generalOptions.EPSG, MapService.EPSG_3857_KEY);
      });
      const feature = editFeature as Feature<Polygon>;
      //Close the polygon, because the coordinates of the polygonOptions do not contain the closure.
      newCoordinates.push(newCoordinates[0]);
      feature.getGeometry().setCoordinates([newCoordinates]);
    }
  }

  /**
   * Function to update the center and radius of a Circle drawing or edit.
   * The modification comes from the form fields in the edit feature panel and is applied to the Circle feature currently being edited.
   */
  manualUpdateCircle() {
    let featureList = this.temporalEditingSource.getFeatures();
    //If there is a feature to update
    if (featureList.length > 0) {
      //Radius always in "meters", so conversion is needed.
      const radius = this.drawingService.convertMetersToRadius(
        this.circleOptions.radius,
        this.mapService.openLayersMap.getView()
      );
      //Transform the coordinates from the EPSG that is specified in options to 3857
      const center = transform(this.circleOptions.center, this.generalOptions.EPSG, MapService.EPSG_3857_KEY);
      const feature = featureList[0] as Feature<Circle>;
      feature.getGeometry().setCenterAndRadius(center, radius);
    }
  }

  /**
   * Function to update the style properties of a Text drawing or edit.
   * The modification comes from the form fields in the edit feature panel and is applied to the styles of the
   * Point feature that represent the text currently being edited.
   */
  manualUpdateText() {
    this.isFeatureEdited = true;
    let featureList = this.temporalEditingSource.getFeatures();
    if (this.drawingInteraction) {
      this.drawingInteraction.getOverlay().setStyle(this.buildTextStyle());
    }
    //If there is a feature to update
    if (featureList.length > 0) {
      const textPosition = transform(this.textOptions.position, this.generalOptions.EPSG, MapService.EPSG_3857_KEY);
      const feature = featureList[0] as Feature<Point>;
      feature.getGeometry().setCoordinates(textPosition);
      feature.setStyle(this.buildTextStyle());
    }
  }

  /**
   * Undo a drawing or modification done with the drawing tool. It uses the operations stack to extract the
   * last modification and apply it.
   * - If the operation is a draw, delete the feature from the temporal drawing layer.
   * - If the operation is a modification, delete the feature from the temporal drawing layer and add the previous version that is stored on the operation.
   */
  undoOperation() {
    let operation = this.operations.pop(); //Extract the last item from the stack.
    let drawingId = operation.featureId; //Unique drawing id to identify it on the temporal drawing tool.
    const layerToDelete = this.temporalDrawingSource.getFeatureById(drawingId);
    if (operation.type == 'DRAW') {
      this.temporalDrawingLayer.getSource().removeFeature(layerToDelete);
    } else if (operation.type == 'MODIFY') {
      const layerToAdd = operation.oldFeature.clone();
      //Set the id to the figure that is being added for trazability (undo operations can be made several times on the same feature).
      layerToAdd.setId(drawingId);
      if (layerToAdd.get(this.GEOMETRY_TYPE_KEY) == DrawingGeometries.TEXT) {
        this.textOptions = safeJsonClone(layerToAdd.get(this.TEXT_PROPERTIES_KEY));
        layerToAdd.setStyle(this.buildTextStyle());
      } else {
        layerToAdd.setStyle(this.styleService.buildStyleFromFeatureStyle(this.drawingService.blueFillFeatureStyle()));
      }
      //Remove the feature and add it again.
      this.temporalDrawingLayer.getSource().removeFeature(layerToDelete);
      this.temporalDrawingLayer.getSource().addFeature(layerToAdd);
    } else if (operation.type == 'DELETE') {
      this.temporalDrawingLayer.getSource().addFeature(operation.oldFeature);
    }
  }

  /**
   * Sets up the drawing tool for editing a feature. It sets up the interactions, options, and variables to edit any of the features that are currently added.
   * @param feature The feature to edit. Comes from the Select interaction.
   */
  setupFeatureToEdit(feature: Feature) {
    //turn on editing mode
    this.isEditing = true;
    this.isFeatureEdited = false;
    //Store the feature that is being edited to be restored in case of cancel, or to save it to the operation stack if success.
    this.editingFeatureCopy = feature.clone();
    this.editingFeatureCopy.setId(feature.getId());
    this.editingFeatureCopy.setStyle(feature.getStyle());
    //select the type of feature
    const featureType = feature.get(this.GEOMETRY_TYPE_KEY);
    let featureToEdit: Feature = null; //Starts at null because if it does not enter any case of the switch, it displays an error.

    switch (featureType) {
      case DrawingGeometries.POINT: {
        this.editDrawingTitle = this.translateService.getTranslateParameter(
          'map.drawing_tool.edit_drawing_title',
          this.figureTranslation['point']
        );
        //Create a new feature with the cloned geometry - This to avoid working on the feature currently added.
        let pointFeature: Feature<Point> = new Feature((feature as Feature<Point>).getGeometry().clone());
        this.pointOptions = {
          coordinate: pointFeature.getGeometry().getCoordinates()
        };
        featureToEdit = pointFeature.clone();
        featureToEdit.setStyle(
          this.styleService.buildStyleFromFeatureStyle(this.drawingService.orangeFillFeatureStyle())
        );
        //Initialize the Modify interaction for editing
        this.modifyInteraction = new Modify({
          source: this.temporalEditingSource,
          style: this.styleService.buildStyleFromFeatureStyle(this.drawingService.blueFillFeatureStyle())
        });
        //When a modification is made
        this.modifyInteraction.on('modifyend', (modify: ModifyEvent) => {
          this.isFeatureEdited = true;
          let featureCollection = modify.features as Collection<Feature>;
          const coord = (featureCollection.item(0) as Feature<Point>).getGeometry().getCoordinates();
          //Save the modified coordinate in the current EPSG set
          this.pointOptions.coordinate = transform(coord, MapService.EPSG_3857_KEY, this.currentEPSG);
        });
        this.mapService.addMapInteraction(this.MODIFY_INTERACTION_KEY, this.modifyInteraction);
        break;
      }

      case DrawingGeometries.LINE: {
        this.editDrawingTitle = this.translateService.getTranslateParameter(
          'map.drawing_tool.edit_drawing_title',
          this.figureTranslation['line']
        );
        //Create a new feature with the cloned geometry
        let lineFeature: Feature<LineString> = new Feature((feature as Feature<LineString>).getGeometry().clone());
        this.lineOptions = {
          coordinates: lineFeature.getGeometry().getCoordinates()
        };
        featureToEdit = lineFeature.clone();
        featureToEdit.setStyle((feat) => {
          return this.buildLineModifyStyle(feat);
        });

        //Initialize the Modify interaction for editing
        this.modifyInteraction = new Modify({
          source: this.temporalEditingSource,
          style: this.styleService.buildStyleFromFeatureStyle(this.drawingService.blueFillFeatureStyle())
        });
        //When a modification is made
        this.modifyInteraction.on('modifyend', (modify: ModifyEvent) => {
          this.isFeatureEdited = true;
          let featureCollection = modify.features as Collection<Feature>;
          const coords = (featureCollection.item(0) as Feature<LineString>).getGeometry().getCoordinates();
          //Set the line coordinates as the currently set EPSG
          this.lineOptions.coordinates = coords.map((coord) => {
            return transform(coord, MapService.EPSG_3857_KEY, this.currentEPSG);
          });
          featureCollection.item(0).setStyle((feat) => {
            return this.buildLineModifyStyle(feat);
          });
        });
        this.mapService.addMapInteraction(this.MODIFY_INTERACTION_KEY, this.modifyInteraction);
        break;
      }

      case DrawingGeometries.POLYGON: {
        this.editDrawingTitle = this.translateService.getTranslateParameter(
          'map.drawing_tool.edit_drawing_title',
          this.figureTranslation['polygon']
        );
        //Create a new feature with the cloned geometry
        let polygonFeature: Feature<Polygon> = new Feature((feature as Feature<Polygon>).getGeometry().clone());
        //Get the inner ring of the polygon
        let coordinatesCopy = polygonFeature.getGeometry().getCoordinates()[0];
        coordinatesCopy.splice(coordinatesCopy.length - 1, 1); //Remove the closure (last point)
        this.polygonOptions = {
          coordinates: coordinatesCopy
        };
        featureToEdit = polygonFeature.clone();
        featureToEdit.setStyle((feat) => {
          return this.buildPolygonModifyStyle(feat);
        });
        //Initialize the Modify interaction for editing
        this.modifyInteraction = new Modify({
          source: this.temporalEditingSource,
          style: this.styleService.buildStyleFromFeatureStyle(this.drawingService.blueFillFeatureStyle())
        });
        //When a modification is made
        this.modifyInteraction.on('modifyend', (modify: ModifyEvent) => {
          this.isFeatureEdited = true;
          let featureCollection = modify.features as Collection<Feature<Polygon>>;
          //Save the coordinates of the internal ring
          let optionsCoordinates = featureCollection.item(0).getGeometry().getCoordinates()[0];
          //Remove the last coordinate (the one that closes the polygon)
          optionsCoordinates.splice(optionsCoordinates.length - 1, 1);
          //Set the polygon coordinates as the currently set EPSG
          this.polygonOptions.coordinates = optionsCoordinates.map((coord) => {
            return transform(coord, MapService.EPSG_3857_KEY, this.currentEPSG);
          });
          featureCollection.item(0).setStyle((feat) => {
            return this.buildPolygonModifyStyle(feat);
          });
        });
        this.mapService.addMapInteraction(this.MODIFY_INTERACTION_KEY, this.modifyInteraction);
        break;
      }

      case DrawingGeometries.CIRCLE: {
        this.editDrawingTitle = this.translateService.getTranslateParameter(
          'map.drawing_tool.edit_drawing_title',
          this.figureTranslation['circle']
        );
        //Create a new feature with the cloned geometry
        let circleFeature: Feature<Circle> = new Feature((feature as Feature<Circle>).getGeometry().clone());
        this.circleOptions = {
          center: circleFeature.getGeometry().getCenter(),
          radius: this.drawingService.convertRadiusToMeters(
            circleFeature.getGeometry().getRadius(),
            this.mapService.openLayersMap.getView()
          )
        };
        featureToEdit = circleFeature.clone();
        featureToEdit.setStyle([
          //Main fill style for the circle
          this.styleService.buildStyleFromFeatureStyle(this.drawingService.orangeFillFeatureStyle()),

          new Style({
            //Style for painting the central point
            geometry: (feature: Feature<Circle>) => {
              return new Point(feature.getGeometry().getCenter());
            },
            image: new CircleStyle({
              radius: 4,
              fill: new Fill({
                color: this.drawingService.circleCenterColor()
              })
            })
          })
        ]);
        //Initialize the Modify interaction for editing.
        this.modifyInteraction = new Modify({
          source: this.temporalEditingSource,
          style: this.styleService.buildStyleFromFeatureStyle(this.drawingService.blueFillFeatureStyle())
        });

        //When a modification is made
        this.modifyInteraction.on('modifyend', (modify: ModifyEvent) => {
          this.isFeatureEdited = true;
          let featureCollection = modify.features as Collection<Feature>;
          const center = (featureCollection.item(0) as Feature<Circle>).getGeometry().getCenter();
          //Save the center to be displayed in the current EPSG
          this.circleOptions.center = transform(center, MapService.EPSG_3857_KEY, this.currentEPSG);
          this.circleOptions.radius = this.drawingService.convertRadiusToMeters(
            (featureCollection.item(0) as Feature<Circle>).getGeometry().getRadius(),
            this.mapService.openLayersMap.getView()
          );
        });
        this.mapService.addMapInteraction(this.MODIFY_INTERACTION_KEY, this.modifyInteraction);
        break;
      }

      case DrawingGeometries.TEXT: {
        this.editDrawingTitle = this.translateService.getTranslateParameter('map.drawing_tool.text_customization');
        let textFeature: Feature<Point> = feature.clone() as Feature<Point>;
        //Fetch the text options from feature properties.
        this.textOptions = textFeature.get(this.TEXT_PROPERTIES_KEY);
        this.textOptions.position = textFeature.getGeometry().getCoordinates();
        featureToEdit = textFeature.clone();
        featureToEdit.setStyle(this.buildTextStyle());
        //Initialize the Modify interaction for editing
        this.modifyInteraction = new Modify({
          source: this.temporalEditingSource,
          style: this.styleService.buildStyleFromFeatureStyle(this.drawingService.blueFillFeatureStyle())
        });
        //When a modification is made
        this.modifyInteraction.on('modifyend', (modify: ModifyEvent) => {
          this.isFeatureEdited = true;
          let featureCollection = modify.features as Collection<Feature>;
          const coord = (featureCollection.item(0) as Feature<Point>).getGeometry().getCoordinates();
          //Save the modified coordinate in the current EPSG set
          this.pointOptions.coordinate = transform(coord, MapService.EPSG_3857_KEY, this.currentEPSG);
          this.textOptions.position = this.pointOptions.coordinate;
        });
        this.mapService.addMapInteraction(this.MODIFY_INTERACTION_KEY, this.modifyInteraction);
        break;
      }

      default:
        break;
    }

    //If the feature is correct - This means a feature to edit has been built and the modify interaction added.
    if (featureToEdit) {
      this.generalOptions = {
        //set the default general options and the geometry name from the feature properties.
        EPSG: this.DEFAULT_EPSG,
        geometryName: feature.get(this.GEOMETRY_NAME_KEY)
      };
      //Add the temporal editing layer as if a new drawing is starting to show the feature to edit.
      this.mapService.openLayersMap.addLayer(this.temporalEditingLayer);
      this.drawingFigure = featureType;
      //Remove the feature to edit from the temporal drawing layer.
      this.temporalDrawingSource.removeFeature(feature);
      //Hide the blue Select interaction style that is applied when a feature is selected.
      this.selectInteraction.getFeatures().forEach((feat) => {
        feat.setStyle(this.transparentStyle);
      });
      this.temporalEditingSource.addFeature(featureToEdit); //Finally add the feature to the layer.
      this.selectInteraction.setActive(false); //Disable the select interaction.
      this.openDrawingEdit();
      this.isDrawing = true;
    }
  }

  /**
   * Method to create the Text Style
   * @method
   * @returns {StyleLike} Style function to render the text with the specified settings
   * @author Joshua Micheletti <j.micheletti@ebw.it>
   */
  buildTextStyle(): StyleLike {
    // make a copy of the current text options
    const textOptionsCopy: {
      label: string;
      fontType: string;
      fontWeight: string;
      fontSize: number;
      fontSizeUnit: string;
      color: string;
      borderColor: string;
      position: number[];
    } = safeJsonClone(this.textOptions);

    // return the stylefunction
    return () => {
      // empty font string setting
      let font = '';

      // if the size unit is meters
      if (textOptionsCopy.fontSizeUnit === 'm') {
        // get the map resolution
        const resolution = this.mapService.openLayersMap.getView().getResolution();
        // calculate the font size by doing the reciprocal of the resolution, multiplied by the font size and by 2.54 (in -> cm)
        const font_size = (1 / resolution) * textOptionsCopy.fontSize * 2.54 * 0.8;
        // create the font setting string
        font = textOptionsCopy.fontWeight + ' ' + font_size + 'px/' + 1 + ' ' + textOptionsCopy.fontType;
      }
      // if the size unit is pixels
      else if (textOptionsCopy.fontSizeUnit === 'px') {
        // create the font setting string
        font = textOptionsCopy.fontWeight + ' ' + textOptionsCopy.fontSize + 'px/' + 1 + ' ' + textOptionsCopy.fontType;
      }

      // create the style object with the specified settings
      const style = new Style({
        text: new Text({
          font: font,
          fill: new Fill({ color: textOptionsCopy.color }),
          stroke: new Stroke({ color: textOptionsCopy.borderColor }),
          text: textOptionsCopy.label
        })
      });

      // return an array containing the style we created
      return [style];
    };
  }

  /**
   * Build an array of OpenLayers Style objects for the Polygon feature when it is in editing/modify mode.
   * This function is also in charge of adding the index of each vertex as overlays.
   * @returns Array of OpenLayers Styles for the polygon feature.
   */
  buildPolygonModifyStyle(feature: FeatureLike): Style[] {
    let polyStyles = [];
    polyStyles.push(
      new Style({
        //Base style
        image: new CircleStyle({
          radius: 5,
          fill: new Fill({
            color: this.drawingService.modifyColor()
          })
        }),
        geometry: (feature: Feature) => {
          //The geometry representation is different depending on the number of points in the feature.
          if (feature.getGeometry().getType() == 'Polygon') {
            const coordinates = (feature.getGeometry() as Polygon).getCoordinates()[0];
            return new MultiPoint(coordinates);
          } else if (feature.getGeometry().getType() == 'Point') {
            const coordinates = (feature.getGeometry() as Point).getCoordinates();
            return new Point(coordinates);
          } else if (feature.getGeometry().getType() == 'LineString') {
            const coordinates = (feature.getGeometry() as LineString).getCoordinates();
            return new MultiPoint(coordinates);
          }
          return null;
        }
      })
    );
    polyStyles.push(this.styleService.buildStyleFromFeatureStyle(this.drawingService.orangeFillFeatureStyle()));

    //Create and add the point index as tooltips (overlays).
    if (feature.getGeometry().getType() == 'Polygon') {
      //Deep copy of the polygon options coordinates
      let polygonCoordinates = this.polygonOptions.coordinates;
      //Remove all tooltips before adding the new ones
      this.removeAllTooltips();
      polygonCoordinates.forEach((coord, i) => {
        //Ensure the coordinates are always EPSG:3857 to add to map!
        let coordsEPSG3857 = transform(coord, this.generalOptions.EPSG, MapService.EPSG_3857_KEY);
        this.createTooltip((i + 1).toString(), coordsEPSG3857);
      });
    }
    return polyStyles;
  }

  /**
   * Build an array of OpenLayers Style objects for the Line feature when it is in editing/modify mode.
   * This function is also in charge of adding the index of each vertex as overlays.
   * @returns Array of OpenLayers Styles for the polygon feature.
   */
  buildLineModifyStyle(feature: FeatureLike): Style[] {
    let lineStyles = [];
    //Main style for painting the polygon orange
    lineStyles.push(this.styleService.buildStyleFromFeatureStyle(this.drawingService.orangeFillFeatureStyle()));
    lineStyles.push(
      new Style({
        image: new CircleStyle({
          radius: 5,
          fill: new Fill({
            color: this.drawingService.modifyColor()
          })
        }),
        geometry: (feature: Feature) => {
          //The geometry representation is different depending on the number of points in the feature.
          if (feature.getGeometry().getType() == 'Point') {
            const coordinates = (feature.getGeometry() as Point).getCoordinates();
            return new Point(coordinates);
          } else if (feature.getGeometry().getType() == 'LineString') {
            const coordinates = (feature.getGeometry() as LineString).getCoordinates();
            return new MultiPoint(coordinates);
          }
          return null;
        }
      })
    );

    //Create and add the point index as tooltips (overlays).
    if (feature.getGeometry().getType() == 'LineString') {
      //Deep copy of the polygon options coordinates
      let lineCoordinates = this.lineOptions.coordinates;
      //Remove all tooltips before adding the new ones
      this.removeAllTooltips();
      lineCoordinates.forEach((coord, i) => {
        //Ensure the coordinates are always EPSG:3857 to add to map!
        let coordsEPSG3857 = transform(coord, this.generalOptions.EPSG, MapService.EPSG_3857_KEY);
        this.createTooltip((i + 1).toString(), coordsEPSG3857);
      });
    }
    return lineStyles;
  }

  /**
   * Creates an overlay for the index number on top of the vertices of Line and Polygon features.
   * The overlay is called tooltip for practical purposes, but is an OpenLayers Overlay.
   * @param text The text to be displayed on the tooltip.
   * @param position The coordinates to locate the tooltip.
   */
  createTooltip(text: string, position: Coordinate) {
    const tooltipElement = document.createElement('div');
    tooltipElement.innerHTML = text;
    //The style is hardcoded into the created div element.
    tooltipElement.style.cssText =
      'background-color:#FFFFFFBB; border:1px solid gray; border-radius:10px; font-weight:bold; padding:0px 5px; margin-bottom:2px;';
    const tooltip = new Overlay({
      //This is the Overlay object.
      element: tooltipElement,
      offset: [0, -6],
      positioning: 'bottom-center',
      position: position
    });
    this.pointTooltips.push(tooltip);
    this.mapService.openLayersMap.addOverlay(tooltip); //Add the overlay to the map.
  }

  /** Close the popup that appears to select between multiple features from the Select interaction. */
  closeSelectPopup() {
    this.drawingSelectPopup.setPosition(undefined);
    this.drawingSelectCloser.blur();
    this.selectInteraction.setActive(true);
    this.selectInteraction.getFeatures().clear();
  }

  /**
   * Function to receive the feature to edit from the selection between more than one features that are overlapping.
   * This triggers the setupFeatureToEdit function with the selected feature from the popup.
   * @param feature The feature that is selected from the Select interaction
   */
  selectFromPopup(feature: Feature) {
    this.removeHoverStyle(feature);
    this.selectedFeatures = [];
    this.closeSelectPopup();
    this.setupFeatureToEdit(feature);
  }

  /**
   * Event handler for the mouseenter event on the feature select popup.
   * It highlights the feature that is being "hovered".
   * @param feature Feature being hovered by the mouse.
   */
  hoverSelected(feature: Feature) {
    const featureStyles = feature.getStyle();
    let stylesToSet: Style[] = [];
    //If the feature has only one style, make it an array of styles. Also for defining the type of the style.
    if (featureStyles instanceof Style) {
      featureStyles as Style;
      stylesToSet = [featureStyles];
    } else if (featureStyles instanceof Array) {
      featureStyles as Style[];
      stylesToSet = featureStyles;
    }
    //If the styles is null, do nothing
    if (!stylesToSet) return;

    if (feature.get(this.GEOMETRY_TYPE_KEY) == DrawingGeometries.TEXT && stylesToSet.length != 0) {
      //The text style is an exception from the other geometries...
      stylesToSet[0]
        .getText()
        ?.setBackgroundStroke(new Stroke({ color: this.drawingService.selectedFeatureColor(), width: 4 }));
    } else {
      //Adds the global selected feature style to highlight the feature in yellow.
      stylesToSet.push(this.selectedFeatureStyle);
    }
    feature.setStyle(stylesToSet); //Set the style with the addition of the yellow highlight.
  }

  /**
   * Event handler for the mouseleave event on the feature select popup.
   * It removes the stlyes that were added to the feature when the mouse was hovering on top of the feature on the list.
   * @param feature Feature that is no longer hovered by the mouse.
   */
  removeHoverStyle(feature: Feature) {
    const featureStyles = feature.getStyle();
    let stylesToSet: Style[] = [];
    //If the style is single, convert it to an array of styles.
    if (featureStyles instanceof Style) {
      featureStyles as Style;
      stylesToSet = [featureStyles];
    } else if (featureStyles instanceof Array) {
      featureStyles as Style[];
      stylesToSet = featureStyles;
    }
    //Exit the function if there is no styles.
    if (!stylesToSet) return;

    if (feature.get(this.GEOMETRY_TYPE_KEY) == DrawingGeometries.TEXT && stylesToSet.length != 0) {
      stylesToSet[0].getText()?.setBackgroundStroke(new Stroke({ color: 'rgba(0,0,0,0.0)', width: 4 }));
    } else {
      //Remove the highlight style from the list of styles of the feature.
      const featureStyleIndex = stylesToSet.findIndex((style) => {
        return style == this.selectedFeatureStyle;
      });
      stylesToSet.splice(featureStyleIndex, 1);
    }
    feature.setStyle(stylesToSet);
  }

  /** Remove all tooltips from the map. Tooltips are the index labels of each vertex in a polygon and line draw/edit. */
  removeAllTooltips() {
    this.pointTooltips.forEach((point) => {
      point.setMap(null);
    });
  }

  /**
   * Checks if the drawing/edit that is ongoing is correct.
   * Depending on the geometry, certain features have different conditions to be correct. In general the feature is correct if
   *  it is not in the drawing phase, but in the modify phase (options coordinates are set).
   *
   * For lines and polygons, a minimum of 2 and 3 points must be already placed, respectively.
   * @returns Whether or not the current drawing/edit is correct.
   */
  checkIfCurrentDrawIsCorrect(): boolean {
    switch (this.drawingFigure) {
      case DrawingGeometries.POINT:
        return this.pointOptions.coordinate[0] != 0 && this.pointOptions.coordinate[1] != 0;
      case DrawingGeometries.LINE:
        return this.lineOptions.coordinates.length > 1; //at least 2 points
      case DrawingGeometries.POLYGON:
        return this.polygonOptions.coordinates.length > 2; //at least 3 points
      case DrawingGeometries.CIRCLE:
        return this.circleOptions.center[0] != 0 && this.circleOptions.center[1] != 0;
      case DrawingGeometries.TEXT:
        return this.textOptions.position[0] != 0 && this.textOptions.position[1] != 0;
      default:
        return false;
    }
  }

  /**
   * Calculate the length / perimeter / circumference of the selected drawing depending on the type.
   * @returns {string} a string representing the length or the selected feature. followed by 'm' or 'km' depending on the size
   */
  calculateLength() {
    // approximate value of PI, used to calculate circumference
    const PI = 3.141592653589;
    // initial value of the output length
    let length = 0;
    let unit = ' m';

    // if we selected a circle, calculate the circumference with the formula 2 * PI * r
    if (this.drawingFigure == this.figureTypes.CIRCLE) {
      length = 2 * PI * this.circleOptions.radius;
    }
    // if it's not a circle
    else {
      // extract the geometry from the vector source
      let geometry;
      try {
        if (this.isEditing) {
          geometry = this.temporalEditingSource.getFeatures()[0].getGeometry();
        } else {
          geometry = (<Feature>this.temporalEditingSource.getFeatureById(this.EDIT_FEATURE_ID)).getGeometry();
        }
      } catch (error) {
        // catch errors in case the geometry hasn't been created yet
        return length.toFixed(4) + unit;
      }

      // extract the length of the geometry (length in case of LineString, perimeter in case of Polygon)
      length = getLength(geometry);
    }

    // switch the measuring unit when the number gets too large
    if (length >= 1000000) {
      length /= 1000;
      unit = ' km';
    }

    // cut the number to the last 4 digits and return it
    return length.toFixed(4) + unit;
  }

  /**
   * Calculate the area of the selected drawing depending on the type
   * @returns {string} a string representing the area of the selected feature. Followed by 'm²' or 'km²' depending on the size
   */
  calculateArea() {
    // approximate value of PI, used to calculate circumference
    const PI = 3.141592653589;
    // initial value of the output area
    let area = 0;
    let unit = ' m²';

    // if we selected a circle, calculate the circumference with the formula PI * r^2
    if (this.drawingFigure == this.figureTypes.CIRCLE) {
      area = PI * this.circleOptions.radius * this.circleOptions.radius;
    }
    // if it's not a circle
    else {
      // extract the geometry from the vector source
      let geometry;
      try {
        if (this.isEditing) {
          geometry = this.temporalEditingSource.getFeatures()[0].getGeometry();
        } else {
          geometry = (<Feature>this.temporalEditingSource.getFeatureById(this.EDIT_FEATURE_ID)).getGeometry();
        }
      } catch (error) {
        // catch errors in case the geometry hasn't been created yet
        return area.toFixed(4) + unit;
      }

      // extract the area of the geometry
      area = getArea(geometry);
    }

    // switch the measuring unit when the number gets too large
    if (area >= 1000000) {
      area /= 1000000;
      unit = ' km²';
    }

    // cut the number to the last 4 digits and return it
    return area.toFixed(4) + unit;
  }
}
